import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  pauseAiScribe,
  resumeAiScribe,
  setStatus,
  setDuration,
  resetDuration,
  closeAiScribeDrawer,
  minimizeAiScribeDrawer,
  setError
} from 'store/clients/clientsSlice';
import { axiosPrivate } from 'modules/networkTools/networkTools';

export const useAiScribe = () => {
  const dispatch = useAppDispatch();
  const {
    state,
    clientName,
    prependedClientId,
    clientId,
    status,
    isPaused,
    duration,
    error: aiScribeError,
    fileUuid,
    lastOrdinalNumber,
    isResumedFromExternalSource
  } = useAppSelector((s) => s.clients.aiScribe);

  // Refs
  const ordinalNumberRef = useRef(lastOrdinalNumber ? lastOrdinalNumber + 1 : 0);
  const durationIntervalRef = useRef<NodeJS.Timeout>();
  const totalElapsedRef = useRef<number>(0);
  const lastStartRef = useRef<number>(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number>();
  const waveformDataRef = useRef<number[]>(Array(50).fill(0));
  const [initializing, setInitializing] = useState(false);
  const savedCanvasImageRef = useRef<{
    imageData: ImageData;
    width: number;
    height: number;
  } | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const dataArrayRef = useRef<Uint8Array | null>(null);
  const failedChunksRef = useRef<Map<number, Blob>>(new Map());
  const retryIntervalRef = useRef<NodeJS.Timeout>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // API Functions
  const initializeRecordingSession = async ({ noteType }: { noteType: string }) => {
    try {
      setInitializing(true);
      const response = await axiosPrivate.put(`/ai-scribe/${clientId}/initialize`, {
        noteType: noteType === '' ? null : noteType
      });
      setInitializing(false);
      dispatch(setError(null));

      return response.data;
    } catch (error) {
      dispatch(setError('System error. Please try again'));
      console.error('Error initializing recording session:', error);
    }
  };

  const uploadChunk = async ({
    recordingSessionId,
    chunk,
    ordinalNumber
  }: {
    recordingSessionId: string;
    chunk: Blob;
    ordinalNumber: number;
  }) => {
    try {
      const formData = new FormData();
      formData.append('chunk', chunk, `${ordinalNumber}.mp4`);

      console.log('Uploading chunk:', ordinalNumber);

      try {
        const searchParams = new URLSearchParams({
          ordinalNumber: ordinalNumber.toString(),
          fileUuid: recordingSessionId.toString(),
          clientId: clientId?.toString() ?? ''
        });
        await axiosPrivate.post(
          `/ai-scribe/${clientId}/upload?${searchParams.toString()}`,
          formData
        );
        dispatch(setError(null));

        // If this was a retry, remove it from failed chunks
        if (failedChunksRef.current.has(ordinalNumber)) {
          failedChunksRef.current.delete(ordinalNumber);
        }
      } catch (error) {
        // Store the failed chunk
        if (!failedChunksRef.current.has(ordinalNumber)) {
          failedChunksRef.current.set(ordinalNumber, chunk);
        }
        dispatch(setError('System error: Consider taking notes.'));
        console.error('Error uploading chunk:', error);
      }
    } catch (error) {
      console.error('Error uploading chunk:', error);
    }
  };

  const retryFailedChunks = async (recordingSessionId: string) => {
    if (failedChunksRef.current.size === 0) return;

    const retryPromises = Array.from(failedChunksRef.current.entries()).map(
      ([ordinalNumber, chunk]) =>
        uploadChunk({
          recordingSessionId,
          chunk,
          ordinalNumber
        })
    );

    try {
      await Promise.all(retryPromises);
    } catch (error) {
      console.error('Error retrying failed chunks:', error);
    }
  };

  const startRetryInterval = (recordingSessionId: string) => {
    // Clear any existing interval
    if (retryIntervalRef.current) {
      clearInterval(retryIntervalRef.current);
    }

    // Start new interval
    retryIntervalRef.current = setInterval(async () => {
      if (failedChunksRef.current.size > 0) {
        await retryFailedChunks(recordingSessionId);
      }
    }, 5000); // Retry every 5 seconds
  };

  const stopRetryInterval = () => {
    if (retryIntervalRef.current) {
      clearInterval(retryIntervalRef.current);
      retryIntervalRef.current = undefined;
    }
  };

  const startTranscription = async (recordingSessionId: string) => {
    try {
      const searchParams = new URLSearchParams({
        fileUuid: recordingSessionId,
        clientId: clientId?.toString() ?? ''
      });
      await axiosPrivate.post(`/ai-scribe/${clientId}/transcribe?${searchParams.toString()}`);
    } catch (error) {
      console.error('Error starting transcription:', error);
    }
  };

  // Recording Functions
  const setupMediaRecorder = async (recordingSessionId: string, shouldResetDuration = false) => {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        autoGainControl: true,
        echoCancellation: true,
        noiseSuppression: true
      }
    });
    streamRef.current = stream;

    // Start retry interval
    startRetryInterval(recordingSessionId);

    // Set up AudioContext for real-time audio analysis
    audioContextRef.current = new AudioContext();
    const source = audioContextRef.current.createMediaStreamSource(stream);
    analyserRef.current = audioContextRef.current.createAnalyser();

    // Configure analyser
    analyserRef.current.fftSize = 256;
    const bufferLength = analyserRef.current.frequencyBinCount;
    dataArrayRef.current = new Uint8Array(bufferLength);

    // Connect audio source to analyser
    source.connect(analyserRef.current);

    // Setup duration tracking
    if (shouldResetDuration) {
      dispatch(resetDuration());
      totalElapsedRef.current = 0;
    } else {
      // For resume scenarios, use existing duration
      totalElapsedRef.current = duration * 1000;
    }
    lastStartRef.current = Date.now();

    // Start duration counter
    durationIntervalRef.current = setInterval(() => {
      const now = Date.now();
      const newDuration = totalElapsedRef.current + (now - lastStartRef.current);
      dispatch(setDuration(newDuration / 1000));
    }, 100);

    const supportedTypes = [
      'audio/webm',
      'audio/webm;codecs=opus',
      'audio/ogg',
      'audio/mp4',
      'audio/wav'
    ];
    let mimeType = '';
    for (const type of supportedTypes) {
      if (MediaRecorder.isTypeSupported(type)) {
        mimeType = type;
        break;
      }
    }
    const mediaRecorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    mediaRecorderRef.current = mediaRecorder;

    mediaRecorder.ondataavailable = async (event) => {
      const mediaRecorderState = (event.currentTarget as MediaRecorder)?.state;
      if (mediaRecorderState === 'inactive') {
        setIsSubmitting(true);
      }

      if (event.data.size > 0) {
        ordinalNumberRef.current += 1;
        await uploadChunk({
          recordingSessionId,
          chunk: event.data,
          ordinalNumber: ordinalNumberRef.current
        });
      }

      if (mediaRecorderState === 'inactive') {
        await startTranscription(recordingSessionId);

        dispatch(setStatus('transcribed'));

        setIsSubmitting(false);
      }
    };

    return mediaRecorder;
  };

  const startRecording = async ({ noteType }: { noteType: string }) => {
    try {
      const data = await initializeRecordingSession({ noteType });

      const recordingSessionId = data.fileUuid;
      if (!recordingSessionId) return;

      // Reset ordinal number for new session
      ordinalNumberRef.current = 0;

      const mediaRecorder = await setupMediaRecorder(recordingSessionId, true);

      dispatch(setStatus('recording'));
      mediaRecorder.start(10000);
    } catch (err) {
      console.error('Error accessing microphone: ', err);
    }
  };

  const stopRecording = async () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();

      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }

      // Stop retry interval
      stopRetryInterval();
    } else if (fileUuid) {
      await startTranscription(fileUuid);
      dispatch(setStatus('transcribed'));
    }

    // Clean up AudioContext
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
      analyserRef.current = null;
      dataArrayRef.current = null;
    }

    // Stop the stream
    if (streamRef.current) {
      streamRef.current?.getTracks().forEach((track) => track.stop());
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      dispatch(pauseAiScribe());
      mediaRecorderRef.current.requestData();

      mediaRecorderRef.current.pause();

      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
        const now = Date.now();
        totalElapsedRef.current += now - lastStartRef.current;
      }

      // Pause retry interval
      stopRetryInterval();
    }
  };

  const resumeRecording = async () => {
    // Handle resume from external source (page refresh scenario)
    if (isResumedFromExternalSource && !mediaRecorderRef.current && fileUuid) {
      try {
        const mediaRecorder = await setupMediaRecorder(fileUuid, false);

        dispatch(resumeAiScribe());
        dispatch(setStatus('recording'));
        mediaRecorder.start(10000);
      } catch (err) {
        console.error('Error resuming recording from external source: ', err);
      }
    }
    // Handle regular resume (existing functionality)
    else if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'paused') {
      dispatch(resumeAiScribe());

      lastStartRef.current = Date.now();
      durationIntervalRef.current = setInterval(() => {
        const now = Date.now();
        const newDuration = totalElapsedRef.current + (now - lastStartRef.current);
        dispatch(setDuration(newDuration / 1000));
      }, 100);

      // Resume retry interval
      startRetryInterval(fileUuid || mediaRecorderRef.current.stream.id);

      mediaRecorderRef.current.resume();
    }
  };

  // Waveform Functions
  const drawCurrentWaveform = (ctx: CanvasRenderingContext2D, rect: DOMRect) => {
    // Bar dimensions
    const barWidth = 2;
    const barGap = 4;
    const barSpacing = barWidth + barGap;
    const maxBars = Math.floor(rect.width / barSpacing);
    const centerY = rect.height / 2;
    const maxBarHeight = rect.height;

    // Generate or update waveform data
    if (status === 'recording' && !isPaused) {
      // Get real audio data from microphone
      if (analyserRef.current && dataArrayRef.current) {
        analyserRef.current.getByteFrequencyData(dataArrayRef.current);

        // Calculate average amplitude from frequency data
        let sum = 0;
        for (let i = 0; i < dataArrayRef.current.length; i++) {
          sum += dataArrayRef.current[i];
        }
        const average = sum / dataArrayRef.current.length;

        // Normalize to 0-1 range and apply sensitivity
        const normalizedValue = (average / 255) * 1.5;
        const newValue = Math.max(0.05, Math.min(1.0, normalizedValue));

        waveformDataRef.current = [...waveformDataRef.current.slice(1), newValue];
      }
    }

    // Draw bars
    for (let i = 0; i < Math.min(maxBars, waveformDataRef.current.length); i++) {
      const x = i * barSpacing;
      const normalizedHeight = waveformDataRef.current[i];
      const barHeight = normalizedHeight * maxBarHeight;
      const y = centerY - barHeight / 2;

      // Create gradient for each bar to ensure proper color distribution
      const barGradient = ctx.createLinearGradient(0, y, 0, y + barHeight);
      barGradient.addColorStop(0.0048, '#DDF4F9');
      barGradient.addColorStop(0.1476, '#57CAE1');
      barGradient.addColorStop(0.5238, '#DBD7F7');
      barGradient.addColorStop(0.9952, '#FFF3F5');

      ctx.fillStyle = barGradient;
      ctx.fillRect(x, y, barWidth, barHeight);
    }
  };

  const drawWaveform = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx || !canvas) return;

    // Set canvas size
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * window.devicePixelRatio;
    canvas.height = rect.height * window.devicePixelRatio;
    ctx.scale(window.devicePixelRatio, window.devicePixelRatio);

    // Restore saved canvas image if available
    if (savedCanvasImageRef.current) {
      // Set canvas to saved dimensions and clear scaling
      ctx.setTransform(1, 0, 0, 1, 0, 0); // Reset transform
      canvas.width = savedCanvasImageRef.current.width;
      canvas.height = savedCanvasImageRef.current.height;

      ctx.putImageData(savedCanvasImageRef.current.imageData, 0, 0);
      savedCanvasImageRef.current = null; // Clear after restoring

      // Continue animation if recording
      if (status === 'recording' && !isPaused) {
        animationFrameRef.current = requestAnimationFrame(drawWaveform);
      }
      return;
    }

    // Clear canvas only if no saved image to restore
    ctx.clearRect(0, 0, rect.width, rect.height);

    // Normal drawing when no saved image
    drawCurrentWaveform(ctx, rect);

    // Continue animation if recording
    if (status === 'recording' && !isPaused) {
      animationFrameRef.current = requestAnimationFrame(drawWaveform);
    }
  };

  // Handle pause/resume from collapsed drawer
  useEffect(() => {
    if (status === 'recording') {
      if (isPaused && mediaRecorderRef.current?.state === 'recording') {
        mediaRecorderRef.current.pause();
        if (durationIntervalRef.current) {
          clearInterval(durationIntervalRef.current);
          const now = Date.now();
          totalElapsedRef.current += now - lastStartRef.current;
        }
      } else if (!isPaused && mediaRecorderRef.current?.state === 'paused') {
        lastStartRef.current = Date.now();
        durationIntervalRef.current = setInterval(() => {
          const now = Date.now();
          const newDuration = totalElapsedRef.current + (now - lastStartRef.current);
          dispatch(setDuration(newDuration / 1000));
        }, 100);
        mediaRecorderRef.current.resume();
      }
    }
  }, [isPaused, status]);

  // Start waveform animation when recording starts or when drawer is expanded
  useEffect(() => {
    if (status === 'recording' && !isPaused && state === 'expanded') {
      drawWaveform();
    } else if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [status, isPaused, state]);

  // Initialize canvas when drawer state changes to expanded
  useEffect(() => {
    // Initial draw
    setTimeout(() => {
      const canvas = canvasRef.current;
      if (canvas && state === 'expanded' && status === 'recording') {
        // Set canvas size
        const rect = canvas.getBoundingClientRect();
        canvas.width = rect.width * window.devicePixelRatio;
        canvas.height = rect.height * window.devicePixelRatio;

        drawWaveform();
      }
    }, 0);
  }, [state]);

  // Save canvas image when collapsing
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    if (state === 'collapsed') {
      // Save the current canvas image data
      try {
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        savedCanvasImageRef.current = {
          imageData,
          width: canvas.width,
          height: canvas.height
        };
      } catch (error) {
        console.warn('Could not save canvas image:', error);
      }
    }
  }, [state]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }
      stopRetryInterval();
    };
  }, []);

  const handleDrawerClose = () => {
    if (status === 'idle' || status === 'transcribed') {
      dispatch(closeAiScribeDrawer());
    } else {
      dispatch(minimizeAiScribeDrawer());
    }
  };

  return {
    // State
    initializing,
    state,
    status,
    isPaused,
    duration,
    clientName,
    prependedClientId,
    error: aiScribeError,
    isSubmitting,
    // Refs
    canvasRef,

    // Functions
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    handleDrawerClose
  };
};

const AiScribeContext = createContext<ReturnType<typeof useAiScribe> | null>(null);

export const AiScribeProvider = ({ children }: { children: React.ReactNode }) => {
  const value = useAiScribe();

  return <AiScribeContext.Provider value={value}>{children}</AiScribeContext.Provider>;
};

export const useAiScribeContext = () => {
  const context = useContext(AiScribeContext);
  if (!context) {
    throw new Error('useAiScribe must be used within an AiScribeProvider');
  }
  return context;
};
